<template>
  <div>
    <PageHeader
      title="Data"
      subtitle="Ingestion, feeds, and imports"
    >
      <template #bottom>
        <Page_Tabs
          class="px-6 pt-4"
          :initial-tab="selected_tab"
          :tabs="etl_tabs"
          no-border
          @select="switch_tab"
        />
      </template>
    </PageHeader>

    <!-- Panes mount on first visit and stay alive (v-show) so tab switches
         never destroy in-progress state (uploads, classification jobs, edits). -->
    <div class="p-6">
      <div v-if="visited.has('Feeds')" v-show="selected_tab === 'Feeds'">
        <Overview_ETL_Feed_Registry @open-ingestion="openIngestion" />
      </div>
      <div v-if="visited.has('Catalog')" v-show="selected_tab === 'Catalog'">
        <Overview_ETL_Data_Catalog />
      </div>
      <div v-if="visited.has('Ingestion')" v-show="selected_tab === 'Ingestion'">
        <Overview_ETL_Ingestion ref="ingestion" @open-agent="openAgent" />
      </div>
      <div v-if="visited.has('Agent')" v-show="selected_tab === 'Agent'">
        <Overview_ETL_Agent ref="agent" />
      </div>
      <div v-if="visited.has('MDR')" v-show="selected_tab === 'MDR'">
        <MDR />
      </div>
      <div v-if="visited.has('Reconciliation')" v-show="selected_tab === 'Reconciliation'">
        <Overview_ETL_Reconciliation />
      </div>
      <div v-if="visited.has('DataSources')" v-show="selected_tab === 'DataSources'">
        <Overview_ETL_Data_Sources />
      </div>
      <div v-if="visited.has('DataAttributes')" v-show="selected_tab === 'DataAttributes'">
        <Overview_ETL_Data_Attributes />
      </div>
      <div v-if="visited.has('Workflows')" v-show="selected_tab === 'Workflows'">
        <Overview_ETL_Workflows ref="workflows" @open="openWorkflow" />
      </div>
      <div v-if="visited.has('Workflow Builder')" v-show="selected_tab === 'Workflow Builder'">
        <Overview_ETL_Workflow_Builder ref="builder" />
      </div>
      <div v-if="visited.has('Uploads')" v-show="selected_tab === 'Uploads'">
        <Customer_File_Upload />
      </div>
    </div>
  </div>
</template>

<script>
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import Overview_ETL_Feed_Registry from '@/components/ETL_Utility/Overview_ETL_Feed_Registry.vue';
import Overview_ETL_Data_Catalog from '@/components/ETL_Utility/Overview_ETL_Data_Catalog.vue';
import Overview_ETL_Ingestion from '@/components/ETL_Utility/Ingestion/Overview_ETL_Ingestion.vue';
import Overview_ETL_Agent from '@/components/ETL_Utility/Overview_ETL_Agent.vue';
import MDR from '@/components/ETL_Utility/mdr/MDR.vue';
import Overview_ETL_Reconciliation from '@/components/ETL_Utility/Reconciliation/Overview_ETL_Reconciliation.vue';
import Overview_ETL_Workflow_Builder from '@/components/ETL_Utility/Overview_ETL_Workflow_Builder.vue';
import Overview_ETL_Workflows from '@/components/ETL_Utility/Overview_ETL_Workflows.vue';
import Overview_ETL_Data_Sources from '@/components/ETL_Utility/Overview_ETL_Data_Sources.vue';
import Overview_ETL_Data_Attributes from '@/components/ETL_Utility/Overview_ETL_Data_Attributes.vue';
import Customer_File_Upload from '@/components/DataUpload/Customer_File_Upload.vue';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';

export default {
  name: 'OverviewListETLUtility',
  components: {
    Page_Tabs,
    PageHeader,
    Overview_ETL_Feed_Registry,
    Overview_ETL_Ingestion,
    Overview_ETL_Agent,
    MDR,
    Overview_ETL_Reconciliation,
    Overview_ETL_Workflow_Builder,
    Overview_ETL_Workflows,
    Overview_ETL_Data_Sources,
    Overview_ETL_Data_Attributes,
    Customer_File_Upload,
    Overview_ETL_Data_Catalog,
  },
  data() {
    return {
      selected_tab: null,
      visited: new Set(),
    };
  },
  computed: {
    etl_tabs() {
      const tabs = [];
      if (canAccessFeature(FEATURES.MODULE_DATA_INGESTION)) {
        tabs.push({ label: 'Agent', icon: ['fas', 'wand-magic-sparkles'] });
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_FEED_REGISTRY)) {
        tabs.push('Feeds');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_CATALOG)) {
        tabs.push('Catalog');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_INGESTION)) {
        tabs.push('Ingestion');
      }
      // Model Import is no longer a tab — it's a modal launched from Uploads
      // (gated there by MODULE_DATA_MODEL_IMPORT).
      if (canAccessFeature(FEATURES.MODULE_DATA_MDR)) {
        tabs.push('MDR');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_RECONCILIATION)) {
        tabs.push('Reconciliation');
      }
      if (canAccessFeature(FEATURES.MODULE_DATA_WORKFLOW_BUILDER)) {
        tabs.push('DataSources', 'DataAttributes', 'Workflows', 'Workflow Builder');
      }
      if (
        canAccessFeature(FEATURES.ACTION_DATA_UPLOAD)
        || canAccessFeature(FEATURES.MODULE_DATA_MODEL_IMPORT)
      ) {
        tabs.push('Uploads');
      }
      return tabs;
    },
  },
  watch: {
    '$route.query.tab'(tab) {
      const labels = this.etl_tabs.map((t) => (typeof t === 'string' ? t : t.label));
      if (tab && tab !== this.selected_tab && labels.includes(tab)) this.switch_tab(tab);
    },
    etl_tabs: {
      handler(newVal) {
        if (newVal.length > 0) {
          // Fall back to the first tab if the remembered one no longer exists
          // (renamed or access revoked) — otherwise the page renders blank.
          const labels = newVal.map((t) => (typeof t === 'string' ? t : t.label));
          const stored = localStorage.getItem('etl_utility_tab');
          const legacy = { 'Data Attributes': 'DataAttributes', Datasets: 'Workflows' };
          const remembered = this.$route.query.tab || legacy[stored] || stored;
          this.selected_tab = labels.includes(remembered) ? remembered : labels[0];
          this.visited.add(this.selected_tab);
        }
      },
      immediate: true,
    },
  },
  methods: {
    switch_tab(tab) {
      const revisit = this.visited.has(tab) && this.selected_tab !== tab;
      this.selected_tab = tab;
      this.visited = new Set([...this.visited, tab]);
      localStorage.setItem('etl_utility_tab', tab);
      if (this.$route.query.tab && this.$route.query.tab !== tab) {
        this.$router.replace({ name: 'Data', query: {} });
      }
      if (revisit && tab === 'Workflows' && this.$refs.workflows) this.$refs.workflows.load();
      if (revisit && tab === 'Workflow Builder' && this.$refs.builder) this.$refs.builder.refreshWorkflows();
    },
    openWorkflow(id) {
      this.switch_tab('Workflow Builder');
      this.$nextTick(() => {
        if (this.$refs.builder) this.$refs.builder.openWorkflow(id);
      });
    },
    // A feed row asked to open its schema/routine in the Ingestion tab. The
    // focus target is already stashed in localStorage by the Feeds component;
    // switch here, then tell the (possibly already-mounted) Ingestion pane to
    // consume it. On first visit the pane's own mounted() consumes it instead —
    // applyPendingFocus is idempotent (reads+clears), so a double call is safe.
    openAgent({ s3Key = '' } = {}) {
      this.switch_tab('Agent');
      this.$nextTick(() => {
        if (this.$refs.agent && this.$refs.agent.openFile) this.$refs.agent.openFile(s3Key);
      });
    },
    openIngestion({ schemaId = null, routineId = null } = {}) {
      localStorage.setItem('etl_ingestion_focus', JSON.stringify({ schemaId, routineId }));
      this.switch_tab('Ingestion');
      this.$nextTick(() => {
        if (this.$refs.ingestion && this.$refs.ingestion.applyPendingFocus) {
          this.$refs.ingestion.applyPendingFocus();
        }
      });
    },
  },
};
</script>
